import { FormLabel } from '@material-ui/core';
import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteRef } from '@shared/components/autocomplete';

import { styles } from './UserSingleLookup.styles';
import { UserLookupCard } from '../user-lookup-card';
import { MixedUser, UserLookup, UserLookupProps } from '../UserLookup';

export interface UserSingleLookupProps extends Omit<UserLookupProps, 'classes'>, WithStyles<typeof styles> {
  showRole?: boolean;
  value?: MixedUser | null;
  onRemove?: () => void;
}

const UserSingleLookupComponent: ForwardRefRenderFunction<AutocompleteRef, UserSingleLookupProps> = (
  { classes, showRole, value, onRemove, ...lookupProps },
  ref
) => {
  const lookupRef = useRef<AutocompleteRef>(null);
  useImperativeHandle(ref, () => lookupRef.current as AutocompleteRef);

  const [localAutoFocus, setLocalAutoFocus] = useState<boolean>(false);

  // Reset the state once we select something
  useEffect(() => {
    if (value) {
      setLocalAutoFocus(false);
    }
  }, [value]);

  return (
    <div className={classes.root}>
      {!!value && (
        <>
          {lookupProps.label && (
            <FormLabel
              classes={{ root: classes.labelRoot, error: classes.labelError }}
              component="legend"
              error={lookupProps.error}
            >
              {lookupProps.label}
            </FormLabel>
          )}
          <UserLookupCard
            inputSize={lookupProps.inputProps?.size}
            item={value}
            showRole={showRole}
            onRemove={
              onRemove && !lookupProps.disabled
                ? () => {
                    if (onRemove) onRemove();

                    setLocalAutoFocus(true);
                  }
                : undefined
            }
          />
        </>
      )}
      {!value && (
        <UserLookup
          {...lookupProps}
          lookupClasses={{ ...lookupProps.lookupClasses, labelRoot: classes.labelRoot, labelError: classes.labelError }}
          inputProps={{ ...lookupProps.inputProps, autoFocus: localAutoFocus || lookupProps.inputProps?.autoFocus }}
          ref={lookupRef}
        />
      )}
    </div>
  );
};

export const UserSingleLookup = withStyles(styles)(forwardRef(UserSingleLookupComponent));
